import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SortIcon from "@mui/icons-material/Sort";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import { PageContainer, PageHeader } from "../components/PageHeader";
import SignageCampaignDialog, {
  type CampaignTarget,
} from "../components/SignageCampaignDialog";
import {
  foyerApi,
  type AssetSummary,
  type MediaLayer,
  type SignageScopeRef,
} from "../http/foyer";
import type {
  SignageCampaign,
  SignageCampaignTargets,
} from "@shared/types/signage";
import { tokens } from "../theme";
import { PermissionGate } from "../auth/access";
import { describeError, useToast } from "../components/toast";
import { signageSaveErrorText } from "../utils/signageRefusal";
import { useFormat, type Formatters } from "../i18n/format";
import type { Translate } from "../i18n/translate";

/**
 * Campaigns - the booking list.
 *
 * The two tabs are the media LAYER, which the model already carries: "Location"
 * is the premise layer (this site's own bookings) and "Company" is the chain's,
 * inherited read-only by every location it owns. That is the same split the
 * media library uses, and the design names it the same way
 * (Platskampanjer / Foretagskampanjer).
 */

export type CampaignScope = "location" | "company";

export interface CampaignRow {
  id: string;
  name: string;
  /** Media filename or a summary when a campaign holds several items. */
  media: string;
  /** `YYYY-MM-DD`, or null for "starts immediately". */
  start: string | null;
  /** `YYYY-MM-DD`, or null for "no end date". */
  end: string | null;
  /** Units it runs on, a booked group's members included (UNIT-4). */
  unitCount: number;
  scope: CampaignScope;
}

export interface SignageCampaignsPageProps {
  rows: CampaignRow[];
  /** Authoring for a whole customer rather than one location. */
  chain?: boolean;
  loading?: boolean;
  onCreate: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

type SortKey = "name" | "media" | "start" | "end" | "unitCount";

function formatDate(
  format: Formatters,
  value: string | null,
  fallback: string
): string {
  if (!value) return fallback;
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return format.date(d, { year: "numeric", month: "short", day: "numeric" });
}

function RowMenu({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(["signage", "common"]);
  const [el, setEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setEl(e.currentTarget)}
        aria-label={t("signageCampaignsPage.actions")}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={el}
        open={Boolean(el)}
        onClose={() => setEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setEl(null);
            onEdit();
          }}
        >
          <EditOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
          {t("common:buttons.edit")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setEl(null);
            onDelete();
          }}
          sx={{ color: tokens.error }}
        >
          <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
          {t("common:buttons.delete")}
        </MenuItem>
      </Menu>
    </>
  );
}

export function SignageCampaignsPage({
  rows,
  chain = false,
  loading,
  onCreate,
  onEdit,
  onDelete,
}: SignageCampaignsPageProps) {
  const { t } = useTranslation(["signage", "common", "glossary"]);
  const format = useFormat();
  const [scope, setScope] = useState<CampaignScope>(
    chain ? "company" : "location"
  );
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [asc, setAsc] = useState(true);

  const visible = useMemo(() => {
    const subset = rows.filter((r) => r.scope === scope);
    const dir = asc ? 1 : -1;
    return [...subset].sort((a, b) => {
      const x = a[sortKey];
      const y = b[sortKey];
      if (x === null) return 1;
      if (y === null) return -1;
      if (typeof x === "number" && typeof y === "number") return (x - y) * dir;
      return String(x).localeCompare(String(y)) * dir;
    });
  }, [rows, scope, sortKey, asc]);

  const head = (key: SortKey, label: string) => (
    <TableCell sortDirection={sortKey === key ? (asc ? "asc" : "desc") : false}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key && !asc ? "desc" : "asc"}
        onClick={() => {
          if (sortKey === key) setAsc((v) => !v);
          else {
            setSortKey(key);
            setAsc(true);
          }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("signageCampaignsPage.eyebrow")}
        title={t("signageCampaignsPage.title")}
        subtitle={t("signageCampaignsPage.subtitle")}
        actions={
          <Stack direction="row" gap={1}>
            <Button variant="outlined" size="small" startIcon={<SortIcon />}>
              {t("signageCampaignsPage.sort")}
            </Button>
            <PermissionGate resource="signage" level="edit">
              <Button
                variant="contained"
                size="small"
                endIcon={<AddIcon />}
                onClick={onCreate}
              >
                {t("signageCampaignsPage.newCampaign")}
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      {!chain && (
        <Box sx={{ borderBottom: `1px solid ${tokens.divider}`, mb: 2 }}>
          <Tabs value={scope} onChange={(_, v) => setScope(v)}>
            <Tab
              value="location"
              label={t("signageCampaignsPage.tabLocation")}
            />
            <Tab value="company" label={t("signageCampaignsPage.tabCompany")} />
          </Tabs>
        </Box>
      )}

      <Box
        sx={{
          border: `1px solid ${tokens.divider}`,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: tokens.surface,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {head("name", t("signageCampaignsPage.columns.name"))}
              {head("media", t("signageCampaignsPage.columns.media"))}
              {head("start", t("signageCampaignsPage.columns.start"))}
              {head("end", t("signageCampaignsPage.columns.end"))}
              {head("unitCount", t("signageCampaignsPage.columns.units"))}
              <TableCell width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
                  >
                    {r.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: tokens.inkMuted }}>{r.media}</TableCell>
                <TableCell sx={{ color: tokens.inkMuted }}>
                  {formatDate(
                    format,
                    r.start,
                    t("signageCampaignsPage.immediately")
                  )}
                </TableCell>
                <TableCell sx={{ color: tokens.inkMuted }}>
                  {/* "No end date" is a real, common state - a campaign that
                      runs until someone stops it. Rendering it as an empty cell
                      would read as missing data. */}
                  {formatDate(
                    format,
                    r.end,
                    t("signageCampaignsPage.noEndDate")
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <TvOutlinedIcon
                      sx={{ fontSize: 18, color: tokens.inkFaint }}
                    />
                    <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
                      {t("glossary:unitCount", { count: r.unitCount })}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <PermissionGate resource="signage" level="edit">
                    <RowMenu
                      onEdit={() => onEdit(r.id)}
                      onDelete={() => onDelete(r.id)}
                    />
                  </PermissionGate>
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Stack alignItems="center" sx={{ py: 5 }} gap={0.5}>
                    {loading && (
                      <Typography
                        variant="bodySm"
                        sx={{ color: tokens.inkMuted }}
                      >
                        {t("common:states.loading")}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontWeight: "fontWeightSemibold",
                        color: tokens.ink,
                      }}
                    >
                      {scope === "company"
                        ? t("signageCampaignsPage.noCompany")
                        : t("signageCampaignsPage.noLocation")}
                    </Typography>
                    <Typography
                      variant="bodySm"
                      sx={{ color: tokens.inkMuted }}
                    >
                      {scope === "company"
                        ? t("signageCampaignsPage.noCompanyHint")
                        : t("signageCampaignsPage.noLocationHint")}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {visible.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderTop: `1px solid ${tokens.divider}` }}
          >
            <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
              {t("signageCampaignsPage.showing", {
                shown: visible.length,
                total: visible.length,
              })}
            </Typography>
          </Stack>
        )}
      </Box>
    </PageContainer>
  );
}

/* -------------------------------------------------------------------------- */

/** Wire shape -> table row. */
function toRow(t: Translate, c: SignageCampaign): CampaignRow {
  return {
    id: c.id,
    name: c.name,
    media:
      c.items.length === 0
        ? t("signage:signageCampaignsPage.noMedia")
        : c.items.length === 1
          ? (c.items[0]!.assetUrl.split("/").pop() ??
            t("signage:signageCampaignsPage.itemCount", { count: 1 }))
          : t("signage:signageCampaignsPage.itemCount", {
              count: c.items.length,
            }),
    start: c.startDate,
    end: c.endDate,
    // Bookings are not stored on the campaign; they are derived from which
    // units and groups list it. Counted from those rather than guessed.
    unitCount: 0,
    scope: c.scope === "premise" ? "location" : "company",
  };
}

/**
 * Campaigns, wired.
 *
 * Both layers arrive in one call - the API already returns a location's own
 * bookings plus the ones its customer shares - so the two tabs are a filter
 * over one list rather than two round trips.
 */
export default function SignageCampaignsContainer({
  premiseId,
  companyId,
}: {
  /** Exactly one is set: a location, or the whole customer. */
  premiseId?: string;
  companyId?: string;
}) {
  const toast = useToast();
  const { t } = useTranslation(["signage", "common", "glossary"]);
  const scope: SignageScopeRef = premiseId
    ? { kind: "premise", premiseId }
    : { kind: "company", companyId: companyId ?? "" };
  const scopeKey = premiseId ?? companyId ?? "";
  const chain = !premiseId;

  const [campaigns, setCampaigns] = useState<SignageCampaign[]>([]);
  /** The units and unit groups this scope may book (UNIT-4). */
  const [targets, setTargets] = useState<CampaignTarget[]>([]);
  /** Which units each group holds, to count the units a campaign reaches. */
  const [groupUnits, setGroupUnits] = useState<Record<string, string[]>>({});
  const [assets, setAssets] = useState<AssetSummary[]>([]);
  /** Where each campaign is booked, keyed by campaign id. */
  const [bookings, setBookings] = useState<
    Record<string, SignageCampaignTargets>
  >({});
  const [loading, setLoading] = useState(true);
  /*
   * The dialog is always mounted with a controlled `open`, so the record it
   * edits is kept while it slides out - clearing it there would flip the edit
   * drawer into the create modal mid-animation. It is reset when New is
   * clicked instead.
   */
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SignageCampaign | null>(null);
  /** A save landed while the surface was open: reload once it has closed. */
  const savedWhileOpen = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    foyerApi
      .listSignageCampaignsIn(scope)
      .then((list) => alive && setCampaigns(list))
      .catch(() => alive && setCampaigns([]))
      .finally(() => alive && setLoading(false));
    // Where each one runs: one read for the whole scope, every location of a
    // customer included, rather than a read per location.
    foyerApi
      .listCampaignTargetsIn(scope)
      .then((map) => alive && setBookings(map))
      .catch(() => alive && setBookings({}));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey, tick]);

  useEffect(() => {
    let alive = true;
    // The groups this scope may book are the ones on its own layer: a
    // location's groups from a location, a customer's from the customer.
    const own = premiseId ? "premise" : "company";
    Promise.all([
      foyerApi.listSignageUnitsIn(scope).catch(() => []),
      foyerApi.listSignageGroupsIn(scope).catch(() => []),
    ]).then(([units, groups]) => {
      if (!alive) return;
      const mine = groups.filter((g) => g.scope === own);
      setGroupUnits(Object.fromEntries(mine.map((g) => [g.id, g.unitIds])));
      setTargets([
        ...mine.map(
          (g): CampaignTarget => ({
            id: `group:${g.id}`,
            kind: "group",
            label: g.name,
            detail: t("signageCampaignsPage.groupDetail", {
              count: g.unitIds.length,
            }),
            premiseLabel: "",
            orientation: g.orientation ?? "landscape",
          })
        ),
        ...units.map(
          (u): CampaignTarget => ({
            id: `unit:${u.key}`,
            kind: "unit",
            label: t("common:labels.unitNumber", { number: u.number }),
            detail: u.screens.length
              ? u.screens.map((x) => x.model ?? x.id).join(", ")
              : t("common:labels.noScreenYet"),
            premiseLabel: premiseId ? "" : u.premiseName,
            orientation: u.orientation,
            assumed: !u.orientationSet && !u.screens.some((x) => x.resolution),
          })
        ),
      ]);
    });
    const layer: MediaLayer = premiseId
      ? { kind: "premise", premiseId }
      : { kind: "company", companyId: companyId ?? "" };
    foyerApi
      .listAssets(layer)
      .then((list) => alive && setAssets(list))
      .catch(() => alive && setAssets([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey, tick, t]);

  const rows = useMemo(
    () =>
      campaigns.map((c) => {
        const b = bookings[c.id];
        // Units reached, a group's members included, each counted once.
        const reached = new Set([
          ...(b?.unitIds ?? []),
          ...(b?.groupIds ?? []).flatMap((g) => groupUnits[g] ?? []),
        ]);
        return { ...toRow(t, c), unitCount: reached.size };
      }),
    [campaigns, bookings, groupUnits, t]
  );

  const bookedForEditing = useMemo(() => {
    const b = editing ? bookings[editing.id] : undefined;
    return b
      ? [
          ...b.unitIds.map((k) => `unit:${k}`),
          ...b.groupIds.map((g) => `group:${g}`),
        ]
      : [];
  }, [editing, bookings]);

  const save = async (
    patch: Partial<SignageCampaign>,
    targetIds: string[]
  ): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      // The campaign first: its id is what the bookings point at, so a failure
      // here must not leave units referring to something that was never
      // created.
      const saved = editing
        ? await foyerApi.updateSignageCampaignIn(scope, editing.id, patch)
        : await foyerApi.createSignageCampaignIn(scope, patch);
      // One call books the whole set, or none of it (UNIT-4).
      await foyerApi.setCampaignTargetsIn(scope, saved.id, {
        unitIds: targetIds
          .filter((id) => id.startsWith("unit:"))
          .map((id) => id.slice("unit:".length)),
        groupIds: targetIds
          .filter((id) => id.startsWith("group:"))
          .map((id) => id.slice("group:".length)),
      });
      // The reload waits for the surface to finish closing: reloading now would
      // re-seed the form under the operator while it slides away.
      savedWhileOpen.current = true;
      const label =
        saved.name || patch.name || t("glossary:campaign_definite_one");
      toast.success(
        editing
          ? t("signageCampaignsPage.updated", { name: label })
          : t("signageCampaignsPage.created", { name: label })
      );
      return true;
    } catch (e) {
      setError(
        signageSaveErrorText(t, e, t("signageCampaignsPage.saveFailed"))
      );
      const name = patch.name || editing?.name;
      toast.error(
        name
          ? describeError(t, "saveCampaignNamed", e, { name })
          : describeError(t, "saveCampaign", e)
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    const previous = campaigns;
    const name = previous.find((c) => c.id === id)?.name;
    const label = name || t("glossary:campaign_definite_one");
    setCampaigns((prev) => prev.filter((c) => c.id !== id));
    try {
      await foyerApi.deleteSignageCampaignIn(scope, id);
      setTick((n) => n + 1);
      toast.success(t("signageCampaignsPage.deleted", { name: label }));
    } catch (e) {
      // Put it back rather than leave the table lying about what exists.
      setCampaigns(previous);
      toast.error(
        name
          ? describeError(t, "deleteCampaignNamed", e, { name })
          : describeError(t, "deleteCampaign", e)
      );
    }
  };

  return (
    <>
      <SignageCampaignsPage
        rows={rows}
        chain={chain}
        loading={loading}
        onCreate={() => {
          setEditing(null);
          setDialogOpen(true);
        }}
        onEdit={(id) => {
          setEditing(campaigns.find((c) => c.id === id) ?? null);
          setDialogOpen(true);
        }}
        onDelete={remove}
      />
      <SignageCampaignDialog
        open={dialogOpen}
        campaign={editing}
        targets={targets}
        bookedTargetIds={bookedForEditing}
        assets={assets}
        onClose={() => {
          setDialogOpen(false);
          if (savedWhileOpen.current) {
            savedWhileOpen.current = false;
            setTick((n) => n + 1);
          }
        }}
        onSave={save}
        saving={saving}
        error={error}
      />
    </>
  );
}
